import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../contexts/AuthContext';
import { useSubscription } from '../../hooks/useSubscription';
import { useStorageQuota } from '../../hooks/useStorageQuota';
import { 
  GraduationCap, 
  FileText, 
  HardDrive, 
  Link2, 
  Calendar, 
  AlertCircle, 
  CheckCircle, 
  Crown,
  Package,
  ArrowRight,
  Sparkles,
  Clock
} from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';

export default function SubscriptionManage() {
  const { user } = useAuthContext();
  const { plan, tier } = useSubscription();
  const { usedGB, limitGB, usagePercent } = useStorageQuota();
  const navigate = useNavigate();
  const [salePageStatus, setSalePageStatus] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    // Fetch Salepage subscription status
    const fetchSalePageStatus = async () => {
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data();
        setSalePageStatus(userData?.salePageSubscription || null);
      } catch (error) {
        console.error('Error fetching salepage status:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSalePageStatus();
  }, [user, navigate]);

  const getDaysUntilExpiry = (expiryDate: any) => {
    if (!expiryDate) return null;
    const expiry = expiryDate.toDate ? expiryDate.toDate() : new Date(expiryDate);
    const now = new Date();
    const diff = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return diff;
  };

  const formatDate = (date: any) => {
    if (!date) return '-';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString('th-TH', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  // Calculate expiry date from subscriptionEndDate or startDate + period
  const getExpiryDate = () => {
    if (user?.subscriptionEndDate) {
      return user.subscriptionEndDate;
    }
    // Calculate from startDate + period if available
    if (user?.subscriptionStartDate && plan?.period) {
      const startDate = user.subscriptionStartDate.toDate ? user.subscriptionStartDate.toDate() : new Date(user.subscriptionStartDate);
      const expiryDate = new Date(startDate);
      if (plan.period === '/เดือน') {
        expiryDate.setMonth(expiryDate.getMonth() + 1);
      } else if (plan.period === '/ปี') {
        expiryDate.setFullYear(expiryDate.getFullYear() + 1);
      }
      return expiryDate;
    }
    return null;
  };

  const courseExpiryDate = getExpiryDate();
  const courseDaysLeft = courseExpiryDate ? getDaysUntilExpiry(courseExpiryDate) : null;
  const salePageDaysLeft = salePageStatus?.expiryDate ? getDaysUntilExpiry(salePageStatus.expiryDate) : null;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">กำลังโหลด...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Package className="w-8 h-8 text-cyan-600" />
            จัดการ Subscription
          </h1>
          <p className="text-gray-600 mt-2">ดูและจัดการแพ็คเก็จทั้งหมดของคุณ</p>
        </div>

        {/* Course Subscription */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6 border-2 border-transparent hover:border-cyan-200 transition-all">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center">
                <GraduationCap className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">คอร์สเรียน</h2>
                <p className="text-sm text-gray-500">แพ็คเก็จ {plan?.name || 'FREE'}</p>
              </div>
            </div>
            {tier !== 'free' && (
              <div className="flex items-center gap-2">
                <Crown className="w-5 h-5 text-amber-500" />
                <span className="text-sm font-semibold text-amber-600">Premium</span>
              </div>
            )}
          </div>

          {/* Package Details */}
          <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-4 mb-4 border border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">รายละเอียดแพ็คเก็จ</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="flex items-start gap-2">
                <Calendar className="w-4 h-4 text-gray-500 mt-0.5" />
                <div>
                  <p className="text-xs text-gray-500">เริ่มใช้งาน</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {user?.subscriptionStartDate ? formatDate(user.subscriptionStartDate) : formatDate(user?.createdAt)}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Calendar className="w-4 h-4 text-gray-500 mt-0.5" />
                <div>
                  <p className="text-xs text-gray-500">หมดอายุ</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {formatDate(courseExpiryDate)}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <HardDrive className="w-4 h-4 text-gray-500 mt-0.5" />
                <div>
                  <p className="text-xs text-gray-500">พื้นที่จัดเก็บ</p>
                  <p className="text-sm font-semibold text-gray-900">{limitGB} GB</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Package className="w-4 h-4 text-gray-500 mt-0.5" />
                <div>
                  <p className="text-xs text-gray-500">ราคา</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {tier === 'free' ? 'ฟรี' : `฿${plan?.price?.toLocaleString()}${plan?.period}`}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Status */}
          {tier === 'free' ? (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <p className="font-semibold text-blue-900">คุณกำลังใช้แพ็คเก็จฟรี</p>
                  <p className="text-sm text-blue-700 mt-1">อัพเกรดเพื่อปลดล็อกฟีเจอร์เพิ่มเติม</p>
                </div>
              </div>
            </div>
          ) : courseDaysLeft !== null && courseDaysLeft <= 7 ? (
            <div className={`${courseDaysLeft <= 3 ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'} border rounded-lg p-4 mb-4`}>
              <div className="flex items-start gap-3">
                <AlertCircle className={`w-5 h-5 ${courseDaysLeft <= 3 ? 'text-red-600' : 'text-amber-600'} mt-0.5`} />
                <div>
                  <p className={`font-semibold ${courseDaysLeft <= 3 ? 'text-red-900' : 'text-amber-900'}`}>
                    {courseDaysLeft <= 0 ? 'หมดอายุแล้ว' : `หมดอายุใน ${courseDaysLeft} วัน`}
                  </p>
                  <p className={`text-sm ${courseDaysLeft <= 3 ? 'text-red-700' : 'text-amber-700'} mt-1`}>
                    วันหมดอายุ: {formatDate(courseExpiryDate)}
                  </p>
                </div>
              </div>
            </div>
          ) : courseExpiryDate ? (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                <div>
                  <p className="font-semibold text-green-900">ใช้งานได้ถึง {formatDate(courseExpiryDate)}</p>
                  {courseDaysLeft && courseDaysLeft > 0 && (
                    <p className="text-sm text-green-700 mt-1">เหลืออีก {courseDaysLeft} วัน</p>
                  )}
                </div>
              </div>
            </div>
          ) : null}

          {/* Storage Usage */}
          <div className="bg-gray-50 rounded-lg p-4 mb-4">
            <div className="flex items-center gap-2 mb-2">
              <HardDrive className="w-4 h-4 text-gray-600" />
              <span className="text-sm font-semibold text-gray-700">พื้นที่จัดเก็บ</span>
            </div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-gray-600">
                {usedGB.toFixed(2)} / {limitGB} GB
              </span>
              <span className="text-sm font-semibold text-cyan-600">
                {usagePercent.toFixed(1)}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
              <div
                className={`h-2 rounded-full transition-all ${
                  usagePercent > 100 ? 'bg-red-500' : 
                  usagePercent > 80 ? 'bg-amber-500' : 
                  'bg-gradient-to-r from-cyan-500 to-blue-600'
                }`}
                style={{ width: `${Math.min(usagePercent, 100)}%` }}
              ></div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-3">
            <Link
              to="/subscription/renew"
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white rounded-lg font-semibold transition-all"
            >
              <Calendar className="w-4 h-4" />
              {tier === 'free' ? 'อัพเกรดเลย' : 'ต่ออายุ / เปลี่ยนแพ็คเก็จ'}
            </Link>
          </div>
        </div>

        {/* Salepage subscription card removed — Sale Page lives on saledi.web.app, separated from edumi.co */}

        {/* Payment History */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Clock className="w-6 h-6 text-gray-600" />
              <h2 className="text-xl font-bold text-gray-900">ประวัติการชำระเงิน</h2>
            </div>
            <Link
              to="/payment/history"
              className="flex items-center gap-2 text-cyan-600 hover:text-cyan-700 font-semibold transition-colors"
            >
              ดูทั้งหมด
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
